import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { GET_FILTERED_POSITIONS } from '../graphql/queries/filteredPositions';
import { GET_EXIT_STRATEGY_TEMPLATES, GET_INVALIDATION_STRATEGY_TEMPLATES } from '../graphql/queries/strategyTemplates';
import { GET_TOKEN_GROUPS } from '../graphql/queries/tokenGroups';
import { GET_TOKEN_LOCATIONS } from '../graphql/queries/tokenLocations';
import { BULK_APPLY_STRATEGIES } from '../graphql/mutations/bulkStrategies';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { formatCurrency } from '../utils/formatters';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { tokenRoutePath } from '../utils/tokenRoutes';

const FILTER_STATE_KEY = 'bulkStrategyFilterState';

// Load initial state from localStorage
const loadSavedFilters = () => {
  try {
    const saved = localStorage.getItem(FILTER_STATE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    // Ignore errors
  }
  return {};
};

const BulkStrategyAssignment = () => {
  const { currentPortfolioId } = usePortfolioContext();
  const savedFilters = useMemo(() => loadSavedFilters(), []);
  const prevPortfolioIdRef = useRef(currentPortfolioId);

  // Filter state (initialized from localStorage)
  const [marketCapFilter, setMarketCapFilter] = useState(savedFilters.marketCapFilter ?? null);
  const [groupFilter, setGroupFilter] = useState(savedFilters.groupFilter ?? null);
  const [locationFilter, setLocationFilter] = useState(savedFilters.locationFilter ?? null);
  const [exitStrategyFilter, setExitStrategyFilter] = useState(savedFilters.exitStrategyFilter ?? null);
  const [invalidationFilter, setInvalidationFilter] = useState(savedFilters.invalidationFilter ?? null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;
  
  // Selection mode: false = all selected, true = selective checkboxes
  const [selectiveMode, setSelectiveMode] = useState(false);
  const [selectedPositions, setSelectedPositions] = useState(new Set());
  
  // Strategy selection
  const [selectedExitTemplate, setSelectedExitTemplate] = useState('');
  const [selectedInvalidationTemplate, setSelectedInvalidationTemplate] = useState('');
  const [allowOverwrite, setAllowOverwrite] = useState(false);
  
  // Confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // Reset apply-to-position filters when portfolio changes (groups/locations are portfolio-specific)
  useEffect(() => {
    if (prevPortfolioIdRef.current !== currentPortfolioId && prevPortfolioIdRef.current != null) {
      setMarketCapFilter(null);
      setGroupFilter(null);
      setLocationFilter(null);
      setExitStrategyFilter(null);
      setInvalidationFilter(null);
      setCurrentPage(0);
      setSelectiveMode(false);
      setSelectedPositions(new Set());
      try {
        localStorage.removeItem(FILTER_STATE_KEY);
      } catch (_) {}
    }
    prevPortfolioIdRef.current = currentPortfolioId;
  }, [currentPortfolioId]);

  // Save filter state to localStorage when it changes
  useEffect(() => {
    try {
      const stateToSave = {
        marketCapFilter,
        groupFilter,
        locationFilter,
        exitStrategyFilter,
        invalidationFilter
      };
      localStorage.setItem(FILTER_STATE_KEY, JSON.stringify(stateToSave));
    } catch (e) {
      // Ignore errors
    }
  }, [marketCapFilter, groupFilter, locationFilter, exitStrategyFilter, invalidationFilter]);

  // Queries
  const { data: exitTemplatesData } = useQuery(GET_EXIT_STRATEGY_TEMPLATES);
  const { data: invalidationTemplatesData } = useQuery(GET_INVALIDATION_STRATEGY_TEMPLATES);
  const { data: groupsData } = useQuery(GET_TOKEN_GROUPS, {
    variables: { portfolioId: currentPortfolioId },
    skip: !currentPortfolioId
  });
  const { data: locationsData } = useQuery(GET_TOKEN_LOCATIONS, {
    variables: { portfolioId: currentPortfolioId },
    skip: !currentPortfolioId
  });

  // Build filter input (high limit so bulk apply can target all filtered positions)
  const filterInput = useMemo(() => ({
    portfolioId: currentPortfolioId,
    marketCapCategory: marketCapFilter,
    groupId: groupFilter,
    locationId: locationFilter,
    hasExitStrategy: exitStrategyFilter,
    hasInvalidationStrategy: invalidationFilter,
    sortBy: 'HOLDINGS_VALUE',
    sortDir: 'DESC',
    limit: 10000,
    offset: 0
  }), [currentPortfolioId, marketCapFilter, groupFilter, locationFilter, exitStrategyFilter, invalidationFilter]);

  const { data: positionsData, loading: positionsLoading, refetch: refetchPositions } = useQuery(GET_FILTERED_POSITIONS, {
    variables: { filter: filterInput },
    skip: !currentPortfolioId,
    fetchPolicy: 'cache-and-network'
  });

  // Mutation
  const [bulkApply, { loading: applyLoading }] = useMutation(BULK_APPLY_STRATEGIES, {
    onCompleted: (data) => {
      const result = data.bulkApplyStrategies;
      if (result.success || result.updatedCount > 0) {
        // Build a combined message
        const parts = [];
        if (result.updatedCount > 0) {
          parts.push(`${result.updatedCount} updated`);
        }
        if (result.skippedCount > 0) {
          parts.push(`${result.skippedCount} skipped`);
        }
        if (result.failedCount > 0) {
          parts.push(`${result.failedCount} failed`);
        }
        const message = parts.join(', ');
        if (result.updatedCount > 0) {
          toast.success(message);
        } else {
          toast(message, { icon: 'ℹ️' });
        }
        refetchPositions();
        setSelectiveMode(false);
        setSelectedPositions(new Set());
      } else {
        toast.error(result.error || 'Failed to apply strategies');
      }
      setShowConfirmModal(false);
      setAllowOverwrite(false);
    },
    onError: (err) => {
      toast.error(err.message);
      setShowConfirmModal(false);
      setAllowOverwrite(false);
    }
  });

  // Reset pagination and selection when filters change
  useEffect(() => {
    setCurrentPage(0);
    setSelectiveMode(false);
    setSelectedPositions(new Set());
  }, [marketCapFilter, groupFilter, locationFilter, exitStrategyFilter, invalidationFilter]);

  const exitTemplates = exitTemplatesData?.exitStrategyTemplates || [];
  const invalidationTemplates = invalidationTemplatesData?.invalidationStrategyTemplates || [];
  const groups = groupsData?.tokenGroups || [];
  const locations = locationsData?.tokenLocations || [];
  const positions = useMemo(
    () => positionsData?.filteredPositions?.positions || [],
    [positionsData?.filteredPositions?.positions]
  );
  const totalCount = positionsData?.filteredPositions?.totalCount || 0;
  const withExitCount = positionsData?.filteredPositions?.withExitStrategy || 0;
  const withInvalidationCount = positionsData?.filteredPositions?.withInvalidationStrategy || 0;

  // Pagination
  const totalPages = Math.ceil(positions.length / pageSize);
  const paginatedPositions = useMemo(() => {
    const start = currentPage * pageSize;
    return positions.slice(start, start + pageSize);
  }, [positions, currentPage, pageSize]);

  // Check if we have any templates
  const hasTemplates = exitTemplates.length > 0 || invalidationTemplates.length > 0;

  // Get the effective position IDs to apply (all if not selective mode, otherwise only selected)
  const effectivePositionIds = useMemo(() => {
    if (selectiveMode) {
      return Array.from(selectedPositions);
    }
    return positions.map(p => p.id);
  }, [selectiveMode, selectedPositions, positions]);

  // Toggle selective mode
  const toggleSelectiveMode = () => {
    if (!selectiveMode) {
      // Entering selective mode - start with all selected
      setSelectedPositions(new Set(positions.map(p => p.id)));
    }
    setSelectiveMode(!selectiveMode);
  };

  // Select all / deselect all (for selective mode)
  const handleSelectAll = () => {
    if (selectedPositions.size === positions.length) {
      setSelectedPositions(new Set());
    } else {
      setSelectedPositions(new Set(positions.map(p => p.id)));
    }
  };

  const togglePosition = (posId) => {
    const newSelected = new Set(selectedPositions);
    if (newSelected.has(posId)) {
      newSelected.delete(posId);
    } else {
      newSelected.add(posId);
    }
    setSelectedPositions(newSelected);
  };

  // Count positions that would be overwritten
  const overwriteCount = useMemo(() => {
    let exitOverwrite = 0;
    let invalidationOverwrite = 0;
    
    const idsToCheck = selectiveMode ? selectedPositions : new Set(positions.map(p => p.id));
    
    positions.forEach(pos => {
      if (!idsToCheck.has(pos.id)) return;
      
      if (selectedExitTemplate && pos.exitStrategy?.levels?.length > 0) {
        exitOverwrite++;
      }
      if (selectedInvalidationTemplate && pos.invalidationLevel) {
        invalidationOverwrite++;
      }
    });
    
    return { exitOverwrite, invalidationOverwrite };
  }, [positions, selectiveMode, selectedPositions, selectedExitTemplate, selectedInvalidationTemplate]);

  const handleApply = () => {
    if (effectivePositionIds.length === 0) {
      toast.error('Please select at least one position');
      return;
    }
    if (!selectedExitTemplate && !selectedInvalidationTemplate) {
      toast.error('Please select at least one strategy template');
      return;
    }
    setShowConfirmModal(true);
  };

  const confirmApply = () => {
    // Build input - use filter for "all" mode, position IDs for selective mode
    const input = {
      portfolioId: currentPortfolioId,
      exitStrategyTemplateId: selectedExitTemplate || null,
      invalidationTemplateId: selectedInvalidationTemplate || null,
      allowOverwrite
    };

    if (selectiveMode) {
      // Selective mode: send explicit position IDs
      input.positionIds = effectivePositionIds;
    } else {
      // All mode: send filter criteria (more efficient for large portfolios)
      input.filter = {
        marketCapCategory: marketCapFilter,
        groupId: groupFilter,
        locationId: locationFilter,
        hasExitStrategy: exitStrategyFilter,
        hasInvalidationStrategy: invalidationFilter
      };
    }

    bulkApply({ variables: { input } });
  };

  const resetFilters = () => {
    setMarketCapFilter(null);
    setGroupFilter(null);
    setLocationFilter(null);
    setExitStrategyFilter(null);
    setInvalidationFilter(null);
  };

  // Empty state if no templates
  if (!hasTemplates) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">📋</div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Create Strategy Templates First
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
          Before you can bulk-apply strategies, you need to create at least one strategy template.
        </p>
        <div className="flex justify-center gap-4">
          <button
            onClick={() => window.location.href = '/strategies?tab=exit'}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create Exit Strategy
          </button>
          <button
            onClick={() => window.location.href = '/strategies?tab=invalidation'}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Create Invalidation Strategy
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Filter positions and apply strategy templates in bulk.
      </p>

      {/* Filters Section */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {/* Market Cap */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Market Cap</label>
            <select
              value={marketCapFilter || ''}
              onChange={(e) => setMarketCapFilter(e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm"
            >
              <option value="">All</option>
              <option value="LARGE_CAP">Large Cap</option>
              <option value="MID_CAP">Mid Cap</option>
              <option value="LOW_CAP">Low Cap</option>
            </select>
          </div>

          {/* Group */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Group</label>
            <select
              value={groupFilter || ''}
              onChange={(e) => setGroupFilter(e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm"
            >
              <option value="">All Groups</option>
              <option value="ungrouped">Ungrouped</option>
              {groups.map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>

          {/* Location */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Location</label>
            <select
              value={locationFilter || ''}
              onChange={(e) => setLocationFilter(e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm"
            >
              <option value="">All Locations</option>
              <option value="unlocated">Unlocated</option>
              {locations.map(l => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>

          {/* Exit Strategy Status */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Exit Strategy</label>
            <select
              value={exitStrategyFilter === null ? '' : exitStrategyFilter.toString()}
              onChange={(e) => {
                const val = e.target.value;
                setExitStrategyFilter(val === '' ? null : val === 'true');
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm"
            >
              <option value="">All</option>
              <option value="true">Has Exit Strategy</option>
              <option value="false">No Exit Strategy</option>
            </select>
          </div>

          {/* Invalidation Status */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Invalidation</label>
            <select
              value={invalidationFilter === null ? '' : invalidationFilter.toString()}
              onChange={(e) => {
                const val = e.target.value;
                setInvalidationFilter(val === '' ? null : val === 'true');
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm"
            >
              <option value="">All</option>
              <option value="true">Has Invalidation</option>
              <option value="false">No Invalidation</option>
            </select>
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <button
            onClick={resetFilters}
            className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Strategy Selection Section */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Apply Strategy</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Exit Strategy Template */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Exit Strategy Template</label>
            <select
              value={selectedExitTemplate}
              onChange={(e) => setSelectedExitTemplate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm"
            >
              <option value="">-- Don't change --</option>
              {exitTemplates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.targetMultiple}x, {t.exitLevelsCount} levels)
                </option>
              ))}
            </select>
          </div>

          {/* Invalidation Template */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Invalidation Strategy Template</label>
            <select
              value={selectedInvalidationTemplate}
              onChange={(e) => setSelectedInvalidationTemplate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm"
            >
              <option value="">-- Don't change --</option>
              {invalidationTemplates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.priceBasedEnabled ? `-${t.priceDropPercentage}%` : ''}{t.priceBasedEnabled && t.timeBasedEnabled ? ' / ' : ''}{t.timeBasedEnabled ? `${t.timeDurationDays}d` : ''})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Apply button */}
        <div className="mt-4 flex items-center justify-end">
          <button
            onClick={handleApply}
            disabled={
              effectivePositionIds.length === 0 || 
              (!selectedExitTemplate && !selectedInvalidationTemplate) ||
              applyLoading
            }
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {applyLoading ? 'Applying...' : `Apply to ${effectivePositionIds.length} Position${effectivePositionIds.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>

      {/* Positions Preview */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 flex justify-between items-center">
          <div className="flex items-center">
            <span className="font-medium text-gray-900 dark:text-white">
              {selectiveMode 
                ? `${selectedPositions.size} of ${totalCount} selected`
                : `All ${totalCount} positions`
              }
            </span>
            <span className="ml-4 text-sm text-gray-500 dark:text-gray-400">
              {withExitCount} with exit, {withInvalidationCount} with invalidation
            </span>
            {positionsLoading && positions.length > 0 && (
              <span className="ml-3 text-xs text-blue-500 dark:text-blue-400 animate-pulse">
                Updating...
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {selectiveMode && (
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400"
              >
                {selectedPositions.size === positions.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
            <button
              onClick={toggleSelectiveMode}
              className={`text-sm px-2 py-1 rounded ${
                selectiveMode 
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' 
                  : 'text-gray-600 hover:text-gray-800 dark:text-gray-400'
              }`}
            >
              {selectiveMode ? 'Exit Selection' : 'Select Specific'}
            </button>
          </div>
        </div>

        {positionsLoading && positions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Loading positions...</div>
        ) : positions.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-2">🔍</div>
            <p className="text-gray-500 dark:text-gray-400">No positions match your filters</p>
            <button
              onClick={resetFilters}
              className="mt-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 text-sm"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className={`overflow-x-auto transition-opacity duration-150 ${positionsLoading ? 'opacity-60' : 'opacity-100'}`}>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  {selectiveMode && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase w-10">
                      <input
                        type="checkbox"
                        checked={selectedPositions.size === positions.length && positions.length > 0}
                        onChange={handleSelectAll}
                        className="rounded"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Token</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Value</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Market Cap</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Exit Strategy</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Invalidation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedPositions.map((pos) => {
                  const hasExit = pos.exitStrategy?.levels?.length > 0;
                  const hasInvalidation = !!pos.invalidationLevel;
                  const willOverwriteExit = selectedExitTemplate && hasExit;
                  const willOverwriteInval = selectedInvalidationTemplate && hasInvalidation;
                  const isSameExitTemplate = willOverwriteExit && pos.exitStrategy?.templateId === selectedExitTemplate;
                  const isSameInvalTemplate = willOverwriteInval && pos.invalidationLevel?.templateId === selectedInvalidationTemplate;
                  const isSelected = selectiveMode ? selectedPositions.has(pos.id) : true;
                  
                  return (
                    <tr 
                      key={pos.id} 
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${
                        selectiveMode && !isSelected ? 'opacity-50' : ''
                      }`}
                    >
                      {selectiveMode && (
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => togglePosition(pos.id)}
                            className="rounded"
                          />
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <Link 
                          to={tokenRoutePath(pos.token)}
                          className="block hover:opacity-80"
                        >
                          <div className="font-medium text-blue-600 dark:text-blue-400 hover:underline">
                            {pos.token?.ticker || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {pos.token?.name}
                          </div>
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-900 dark:text-white">
                        {formatCurrency(pos.holdingsValue)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          pos.token?.marketData?.marketCapCategory === 'Large Cap' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : pos.token?.marketData?.marketCapCategory === 'Mid Cap'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}>
                          {pos.token?.marketData?.marketCapCategory || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {hasExit ? (
                          <span className={`px-2 py-0.5 text-xs rounded inline-flex items-center gap-1 ${
                            willOverwriteExit && isSelected
                              ? isSameExitTemplate
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                              : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          }`}>
                            {pos.exitStrategy?.templateName || 'Custom'}
                            {willOverwriteExit && isSelected && (
                              isSameExitTemplate 
                                ? <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                : <span>⚠️</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">None</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {hasInvalidation ? (
                          <span className={`px-2 py-0.5 text-xs rounded inline-flex items-center gap-1 ${
                            willOverwriteInval && isSelected
                              ? isSameInvalTemplate
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                              : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                          }`}>
                            {pos.invalidationLevel?.templateName || 'Custom'}
                            {willOverwriteInval && isSelected && (
                              isSameInvalTemplate
                                ? <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                : <span>⚠️</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">None</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, positions.length)} of {positions.length}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-600 dark:text-gray-300">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <ConfirmDeleteModal
          title="Apply Strategies"
          message={
            <div className="space-y-2">
              <p>You are about to apply strategies to {effectivePositionIds.length} positions</p>
              {selectedExitTemplate && (
                <p className="text-sm">
                  <strong>Exit Strategy:</strong> {exitTemplates.find(t => t.id === selectedExitTemplate)?.name}
                </p>
              )}
              {selectedInvalidationTemplate && (
                <p className="text-sm">
                  <strong>Invalidation Strategy:</strong> {invalidationTemplates.find(t => t.id === selectedInvalidationTemplate)?.name}
                </p>
              )}
              {(overwriteCount.exitOverwrite > 0 || overwriteCount.invalidationOverwrite > 0) && (
                <label className="flex items-center mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-sm text-amber-700 dark:text-amber-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allowOverwrite}
                    onChange={(e) => setAllowOverwrite(e.target.checked)}
                    className="mr-2 rounded border-amber-400"
                  />
                  <span>
                    I confirm overwriting {[
                      overwriteCount.exitOverwrite > 0 && `${overwriteCount.exitOverwrite} exit`,
                      overwriteCount.invalidationOverwrite > 0 && `${overwriteCount.invalidationOverwrite} invalidation`
                    ].filter(Boolean).join(' and ')} strategies
                  </span>
                </label>
              )}
              <p className="text-sm text-gray-500 mt-2">This action cannot be undone.</p>
            </div>
          }
          confirmText="Apply Strategies"
          confirmButtonClass="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none transition-colors"
          confirmDisabled={(overwriteCount.exitOverwrite > 0 || overwriteCount.invalidationOverwrite > 0) && !allowOverwrite}
          onConfirm={confirmApply}
          onCancel={() => {
            setShowConfirmModal(false);
            setAllowOverwrite(false);
          }}
        />
      )}
    </div>
  );
};

export default BulkStrategyAssignment;
